import { lock } from '@weareberyl/design-system/build/images/icons/generated'
import { Select } from 'antd'
import { BaseOptionType } from 'antd/lib/select'
import type { ComponentProps } from 'react'
import {
  LockConfiguration,
  ParkingStatus,
  SchemePricingConfigs,
  VehicleLockState,
} from '~/gql/generated/graphql'

type SelectWithFinesProps = ComponentProps<typeof Select> & {
  schemePricingConfigs: SchemePricingConfigs
}
export const ParkingStatusWithFinesSelect = ({
  schemePricingConfigs: {
    out_of_service_area_parking_charge,
    out_of_zone_parking_charge,
  },
  ...props
}: SelectWithFinesProps) => {
  return (
    <Select
      allowClear
      placeholder="Auto"
      options={[
        { value: ParkingStatus.valid, label: 'Valid: No fine' },
        {
          value: ParkingStatus.out_of_service_area,
          label: `Out of service area: ${out_of_service_area_parking_charge.formatted_amount} fee`,
        },
        {
          value: ParkingStatus.out_of_zone,
          label: `Out of bay: ${out_of_zone_parking_charge.formatted_amount} fee`,
        },
      ]}
      {...props}
    />
  )
}

export const VehicleLockStateWithFinesSelect = ({
  schemePricingConfigs: {
    undocked_in_zone_parking_charge,
    rear_lock_unlocked_vehicle_charge,
  },
  lockConfiguration,
  ...props
}: SelectWithFinesProps & {
  lockConfiguration?: LockConfiguration
}) => {
  const options: BaseOptionType[] = [
    { value: VehicleLockState.locked, label: 'Locked: No fine' },
  ]

  // If the bike has both manual locks,
  // there's a chance it could be parked with the front lock only and the
  // only way to secure it is for the user to lock the rear lock.
  // If they don't do that, on this type of vehicle, they could be fined, if configured.
  if (lockConfiguration === LockConfiguration.front_and_rear_lock) {
    options.push({
      value: VehicleLockState.front_lock_only,
      label: `Front lock only: ${
        rear_lock_unlocked_vehicle_charge &&
        rear_lock_unlocked_vehicle_charge?.amount > 0
          ? rear_lock_unlocked_vehicle_charge?.formatted_amount
          : 'no'
      } fee`,
    })
  }

  // If there is any type of front lock, and we enforce mandatory frontlocking
  // then locking with rear lock only would cause a fine. This is mostly not enforced
  if (
    lockConfiguration &&
    [
      LockConfiguration.front_and_rear_lock,
      LockConfiguration.front_and_electronic_rear_lock,
    ].includes(lockConfiguration)
  ) {
    options.push({
      value: VehicleLockState.rear_lock_only,
      label: `Rear lock only: ${
        undocked_in_zone_parking_charge &&
        undocked_in_zone_parking_charge?.amount > 0
          ? undocked_in_zone_parking_charge?.formatted_amount
          : 'no'
      } fee`,
    })
  }

  // No point setting it as unlocked here, as that would probably constitute a flat fee
  // TODO: Check on account to see if this is handled in charge worker. I think this would
  // be covered by a job auto doing cancelled journey
  options.push({
    value: VehicleLockState.unlocked,
    label: 'For unlocked, use cancel journey',
    disabled: true,
  })

  return <Select allowClear placeholder="Auto" options={options} {...props} />
}
